/**
 * BgMatting — background segmentation for browser tools.
 *
 * Replaces the @imgly/background-removal path, which had three problems this
 * module exists to fix:
 *
 *  1. LICENCE. @imgly/background-removal is AGPL-3.0 with a "contact us for
 *     other licensing options" note — network copyleft, triggered by exactly
 *     what we do (serve it to browsers from a commercial site).
 *  2. RELIABILITY. It fetches its own onnxruntime from esm.sh at run time and
 *     ships only a SIMD WASM artefact. Our telemetry for /text-behind-image
 *     (the other consumer) shows those two as the dominant failures: "WebAssembly
 *     SIMD is not supported" and "Failed to fetch dynamically imported module:
 *     https://esm.sh/onnxruntime-web@...". We self-host both ORT artefacts and
 *     fall back to the non-SIMD one, which is a path imgly cannot take.
 *  3. DELIVERY. Its weights were reachable only through an nginx alias that
 *     exists on the RU box and NOT on NL, so the tool could never have worked
 *     on the eight international locales.
 *
 * Public surface (classic script, sets window.BgMatting):
 *
 *   const eng = BgMatting.create({ slug, urls });
 *   await eng.prepare('balanced', onProgress);        // download + session
 *   const r = await eng.cut(bitmap, { maxSide, refine, onStage });
 *   // r = { alpha, fg, w, h, provider, inferMs, refineMs }
 *
 * `alpha` is Float32Array(w*h) 0..1. `fg` is Float32Array(w*h*3) 0..1 with the
 * background colour un-mixed (see refine-core.js) or null when refine:false.
 */
(function (root) {
'use strict';

var ONNX_CACHE = 'onnx-models';   // owned by public/sw.js — do not invent a second one

/* ── capability probe ─────────────────────────────────────────────────── */

var _caps = null;
async function capabilities() {
    if (_caps) return _caps;
    var webgpu = false;
    try {
        if (root.navigator && root.navigator.gpu) {
            var ad = await root.navigator.gpu.requestAdapter();
            webgpu = !!ad;
        }
    } catch (_) {}
    // WebAssembly SIMD: the exact capability whose absence kills the old
    // engine. Probed by validating the shortest module that uses v128.
    var simd = false;
    try {
        simd = WebAssembly.validate(new Uint8Array([
            0, 97, 115, 109, 1, 0, 0, 0, 1, 5, 1, 96, 0, 1, 123, 3, 2, 1, 0,
            10, 10, 1, 8, 0, 65, 0, 253, 15, 253, 98, 11
        ]));
    } catch (_) {}
    _caps = {
        webgpu: webgpu,
        simd: simd,
        memGB: (root.navigator && root.navigator.deviceMemory) || 0,
        cores: (root.navigator && root.navigator.hardwareConcurrency) || 2,
    };
    return _caps;
}

/* ── model cache state ────────────────────────────────────────────────── */

/** Has this exact model URL already been downloaded? Drives the copy the
 *  user sees before the first run ("44 MB once" vs "ready"). */
async function isCached(url) {
    try {
        if (!root.caches) return false;
        var c = await root.caches.open(ONNX_CACHE);
        var hit = await c.match(url, { ignoreSearch: false });
        return !!hit;
    } catch (_) { return false; }
}

/** The SHARED resumable downloader (public/js/model-download.js).
 *
 *  A dropped mobile connection is the norm, not an edge case: 173 sessions
 *  across eight tools died mid-model over 30 days, at wildly different offsets.
 *  Five hand-rolled streaming fetches were written and re-fixed independently
 *  before this block existed; this module is not becoming the sixth, and
 *  ModelDownloadResumeTest holds that line. */
function loadDownloader() {
    if (root.__mdFetchBytes) return Promise.resolve(root.__mdFetchBytes);
    return new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = '/js/model-download.js';
        s.onload = function () {
            root.__mdFetchBytes ? resolve(root.__mdFetchBytes)
                                : reject(new Error('model-download loaded but __mdFetchBytes missing'));
        };
        s.onerror = function () { reject(new Error('model-download.js failed to load')); };
        document.head.appendChild(s);
    });
}

async function fetchWithProgress(url, fallbackUrl, onProgress, signal) {
    var mdFetchBytes = await loadDownloader();
    var urls = fallbackUrl && fallbackUrl !== url ? [url, fallbackUrl] : [url];
    var lastErr = null;
    for (var i = 0; i < urls.length; i++) {
        try {
            var bytes = await mdFetchBytes(urls[i], {
                signal: signal,
                onProgress: function (received, total) { if (onProgress) onProgress(received, total); },
            });
            return bytes.buffer;
        } catch (e) {
            if (signal && signal.aborted) throw e;
            lastErr = e;
        }
    }
    throw lastErr || new Error('model_fetch_failed');
}

/* ── engine ───────────────────────────────────────────────────────────── */

function create(cfg) {
    cfg = cfg || {};
    var urls = cfg.urls || {};
    var models = urls.models || {};
    var worker = null, workerReady = false, activeModel = null;
    var pending = {}, tokenSeq = 0;
    var bootPromise = null;

    function telemetry(action, payload) {
        try {
            if (typeof root.__ae === 'function') {
                root.__ae('feature_use', Object.assign({ slug: cfg.slug || 'bg-matting', action: action }, payload || {}));
            }
        } catch (_) {}
    }

    function spawn() {
        var w = new Worker(urls.worker);
        w.onmessage = function (ev) {
            var m = ev.data || {};
            if (m.type === 'note') { telemetry('matting_' + m.note, {}); return; }
            var slot = m.token != null ? pending[m.token] : pending.__boot;
            if (!slot) return;
            if (m.token != null) delete pending[m.token]; else delete pending.__boot;
            if (m.type === 'error') slot.reject(new Error(m.code || 'worker_error'));
            else slot.resolve(m);
        };
        w.onerror = function (e) {
            var slot = pending.__boot;
            if (slot) { delete pending.__boot; slot.reject(new Error('worker_boot: ' + (e.message || ''))); }
        };
        return w;
    }

    /** Which onnxruntime build this device needs.
     *
     *  onnxruntime-web dropped its non-SIMD wasm in 1.19, so on a CPU whose V8
     *  refuses WebAssembly SIMD the modern runtime has NO backend at all and
     *  every run dies with "no available backend found" — the single most
     *  common failure recorded against the engine this module replaces. 1.18 is
     *  the last release shipping ort-wasm.wasm and we self-host it; the same
     *  split is already in production in video-bg-remover/rvm-engine.js.
     *  ?ortlegacy=1 forces the path so a test can prove it on normal hardware. */
    async function runtimeFor() {
        var caps = await capabilities();
        var forced = false;
        try { forced = /[?&]ortlegacy=1/.test(root.location.search); } catch (_) {}
        /* The same 1.18, WITH SIMD, for a browser that cannot reserve the shared
           4 GiB memory the current build imports (WebKit before 18: «no available
           backend found. ERR: [wasm] RangeError: Out of memory»). The owner
           (window.__ortBuild) learns it from the first failure — prepare() below
           tells it — and remembers. */
        var owner = root.__ortBuild, L = null;
        try { L = (caps.simd && owner && owner.noShare && owner.noShare() && owner.legacy) ? owner.legacy() : null; } catch (_) {}
        if (L && L.simd && !forced) {
            return { ort: L.url, wasmBase: L.wasmBase, simd: true, legacy: true, webgpu: false };
        }
        if (!caps.simd || forced) {
            return { ort: urls.ortLegacy || '/vendor/onnxruntime-legacy/ort.min.js',
                     wasmBase: urls.wasmBaseLegacy || '/vendor/onnxruntime-legacy/',
                     simd: false, webgpu: false };
        }
        return { ort: urls.ort, wasmBase: urls.wasmBase, simd: true, webgpu: caps.webgpu };
    }

    /** Pre-fetch ort.min.js on the main thread and hand the worker a Blob URL.
     *  importScripts() on a network URL inside a worker can stall past 30 s
     *  with no error — root-caused on apollo (c2f246d5d) and re-confirmed on
     *  ai-upscale. A blob: read cannot hang on the network. */
    async function ortBlob(url, signal) {
        try {
            var res = await fetch(url, { signal: signal, credentials: 'omit' });
            if (!res.ok) return null;
            return URL.createObjectURL(await res.blob());
        } catch (_) { return null; }
    }

    var ranOnLegacy = false;
    async function prepare(modelKey, onProgress, signal) {
        try { return await prepareOnce(modelKey, onProgress, signal); }
        catch (e) {
            /* Not the device running out of memory — the reservation failure above.
               prepareOnce has already dropped the worker that failed; once more, on
               1.18 (the model is in the cache), after a pause: an immediate retry
               meets the same address space. */
            var owner = root.__ortBuild;
            if ((signal && signal.aborted)
                || !(owner && owner.initFailed && owner.initFailed(e, { legacy: ranOnLegacy ? 'legacy' : '', from: 'matting' }))) throw e;
            await new Promise(function (r) { setTimeout(r, 1000); });
            return prepareOnce(modelKey, onProgress, signal);
        }
    }
    async function prepareOnce(modelKey, onProgress, signal) {
        var spec = models[modelKey];
        if (!spec) throw new Error('unknown_model_' + modelKey);
        if (workerReady && activeModel === modelKey) return { cached: true, provider: null };
        if (bootPromise) { try { await bootPromise; } catch (_) {} }

        bootPromise = (async function () {
            var cached = await isCached(spec.url);
            if (onProgress) onProgress({ stage: cached ? 'model-cached' : 'model-download', loaded: 0, total: spec.bytes || 0 });

            var t0 = Date.now();
            var bytes = await fetchWithProgress(spec.url, spec.fallbackUrl, function (loaded, total) {
                if (onProgress) onProgress({ stage: 'model-download', loaded: loaded, total: total || spec.bytes || 0 });
            }, signal);
            var dlMs = Date.now() - t0;

            if (onProgress) onProgress({ stage: 'session', loaded: 1, total: 1 });
            if (!worker) worker = spawn();
            var rt = await runtimeFor();
            ranOnLegacy = !!rt.legacy || !rt.simd;
            var blobUrl = await ortBlob(rt.ort, signal);

            var ready = await new Promise(function (resolve, reject) {
                pending.__boot = { resolve: resolve, reject: reject };
                worker.postMessage({
                    type: 'init', modelBytes: bytes, modelKey: modelKey,
                    ortBlobUrl: blobUrl, ortUrl: rt.ort, wasmBase: rt.wasmBase, simd: rt.simd, legacy: ranOnLegacy,
                    webgpu: rt.webgpu && spec.webgpu !== false,
                }, [bytes]);
            });
            if (blobUrl) { try { URL.revokeObjectURL(blobUrl); } catch (_) {} }
            workerReady = true; activeModel = modelKey;
            telemetry('matting_ready', { model: modelKey, provider: ready.provider, cached: cached ? 1 : 0,
                                         dl_ms: dlMs, legacy: rt.legacy ? 2 : (rt.simd ? 0 : 1) });
            return { cached: cached, provider: ready.provider, dlMs: dlMs, legacy: !rt.simd };
        })();

        try { return await bootPromise; }
        catch (e) {
            workerReady = false; activeModel = null;
            if (worker) { try { worker.terminate(); } catch (_) {} worker = null; }
            telemetry('matting_failed', { model: modelKey, err: String(e && e.message || e).slice(0, 120) });
            throw e;
        }
        finally { bootPromise = null; }
    }

    /** RGBA of a source, downscaled so the long side is at most `maxSide`. */
    function readPixels(source, maxSide) {
        var sw = source.naturalWidth || source.videoWidth || source.width;
        var sh = source.naturalHeight || source.videoHeight || source.height;
        var scale = maxSide && Math.max(sw, sh) > maxSide ? maxSide / Math.max(sw, sh) : 1;
        var w = Math.max(1, Math.round(sw * scale)), h = Math.max(1, Math.round(sh * scale));
        var cv = (typeof OffscreenCanvas !== 'undefined') ? new OffscreenCanvas(w, h)
                                                          : Object.assign(document.createElement('canvas'), { width: w, height: h });
        var ctx = cv.getContext('2d', { willReadFrequently: true });
        ctx.drawImage(source, 0, 0, w, h);
        return { rgba: ctx.getImageData(0, 0, w, h).data, w: w, h: h, srcW: sw, srcH: sh };
    }

    async function cut(source, opts) {
        opts = opts || {};
        if (!workerReady) throw new Error('not_prepared');
        var spec = models[activeModel];
        var onStage = opts.onStage || function () {};

        onStage('read');
        // The model sees a square at its native size; feeding it more pixels
        // than that buys nothing, so we read at a bounded size and upsample
        // the mask afterwards against the FULL-resolution image.
        var refineSide = Math.max(512, Math.min(opts.maxSide || 1536, 2048));
        var src = readPixels(source, refineSide);

        onStage('infer');
        var token = ++tokenSeq;
        var rgbaCopy = new Uint8ClampedArray(src.rgba);       // transferred away
        var res = await new Promise(function (resolve, reject) {
            pending[token] = { resolve: resolve, reject: reject };
            worker.postMessage({
                type: 'run', token: token, rgba: rgbaCopy.buffer, w: src.w, h: src.h,
                size: spec.size, mean: spec.mean, std: spec.std, post: spec.post,
                outputName: spec.outputName || null,
            }, [rgbaCopy.buffer]);
        });

        var R = root.BgRefine;
        var maskSq = new Float32Array(res.mask);
        onStage('upscale');
        var alpha = R.resizeBilinear(maskSq, res.size, res.size, src.w, src.h, 1);
        if (opts.sharpen) alpha = R.sharpenAlpha(alpha, opts.sharpen);

        var fg = null, refineMs = 0;
        if (opts.refine !== false) {
            onStage('refine');
            var rgb = new Float32Array(src.w * src.h * 3);
            for (var p = 0, p3 = 0, p4 = 0; p < src.w * src.h; p++, p3 += 3, p4 += 4) {
                rgb[p3] = src.rgba[p4] / 255; rgb[p3 + 1] = src.rgba[p4 + 1] / 255; rgb[p3 + 2] = src.rgba[p4 + 2] / 255;
            }
            var t1 = Date.now();
            fg = R.estimateForeground(rgb, alpha, src.w, src.h, {});
            refineMs = Date.now() - t1;
        }

        return {
            alpha: alpha, fg: fg, w: src.w, h: src.h, srcW: src.srcW, srcH: src.srcH,
            provider: res.provider, inferMs: res.ms, refineMs: refineMs,
        };
    }

    function dispose() {
        if (worker) { try { worker.terminate(); } catch (_) {} }
        worker = null; workerReady = false; activeModel = null; pending = {};
    }

    return {
        prepare: prepare, cut: cut, dispose: dispose,
        capabilities: capabilities, isCached: function (k) { return isCached((models[k] || {}).url); },
        get provider() { return activeModel; },
    };
}

root.BgMatting = { create: create, capabilities: capabilities };

})(typeof window !== 'undefined' ? window : self);
